import { useState, useEffect, useRef } from 'react';
import { google, outlook, ics } from 'calendar-link';
import type { CalendarEvent } from 'calendar-link';
import styles from '../styles/widget.module.css';
import type { BookingState } from '../state/machine';

// calendar-link does not have a dedicated "Apple Calendar" provider.
// Both "Apple Calendar" and "iCal" use the ics() output (a data:text/calendar
// URI) and trigger a .ics file download, which macOS/iOS open in Calendar.app.
// Yahoo Calendar and AOL are not shown — not in the original provider set.

interface AddToCalendarProps {
  state: BookingState;
  className?: string;
}

function isoToHHMM(iso: string): string {
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[1]}:${m[2]}` : '';
}

const AddToCalendar = ({ state, className }: AddToCalendarProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { selectedDate, selectedSlot, selectedLocation, selectedFittingType, confirmation } = state;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!selectedDate || !selectedSlot) return null;

  const startTime = isoToHHMM(selectedSlot.start);
  const endTime = isoToHHMM(selectedSlot.end);
  if (!startTime || !endTime) return null;

  const fittingLabel = selectedFittingType?.displayName ?? selectedFittingType?.name ?? 'Fitting';
  const locationStr = [selectedLocation?.name, selectedLocation?.address].filter(Boolean).join(', ');
  const descParts: string[] = [];
  if (selectedSlot.fitterName) descParts.push(`Fitter: ${selectedSlot.fitterName}`);
  if (confirmation?.id) descParts.push(`Confirmation: #${confirmation.id}`);

  // Naive local datetime strings (no "Z") so calendar-link treats them as
  // browser-local time, matching the widget's AS-IS slot display. The
  // middleware stores store-local times labeled as UTC; see datetime.ts.
  const event: CalendarEvent = {
    title: `Club Champion ${fittingLabel}`,
    start: `${selectedDate}T${startTime}:00`,
    end: `${selectedDate}T${endTime}:00`,
    location: locationStr || undefined,
    description: descParts.join(' | ') || undefined,
    status: 'CONFIRMED',
  };

  // ics() already returns a data:text/calendar URI — use directly as href.
  const icsHref = ics(event);

  const options: Array<{ label: string; href: string; download?: string }> = [
    { label: 'Google Calendar', href: google(event) },
    { label: 'Apple Calendar', href: icsHref, download: 'event.ics' },
    { label: 'Outlook.com', href: outlook(event) },
    { label: 'iCal', href: icsHref, download: 'event.ics' },
  ];

  return (
    <div className={`${styles.addToCalendarWrapper} ${className}`} ref={wrapperRef}>
      <button
        type="button"
        className={styles.addToCalendar}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        Add to Calendar
      </button>
      {open && (
        <ul className={styles.atcDropdown} role="listbox">
          {options.map(({ label, href, download }) => (
            <li key={label} role="option">
              <a
                href={href}
                target={download ? undefined : '_blank'}
                rel={download ? undefined : 'noopener noreferrer'}
                download={download}
                onClick={() => setOpen(false)}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddToCalendar;
